// Overview dashboard — the at-a-glance control-plane home: fleet KPIs, revenue,
// trials, over-limit customers, service health, real charts (status donut, MRR
// by plan, top customers, provisioning trend) and a recent-activity feed. Built
// from existing /system/* endpoints (overview + health-deep + audit) with
// recharts, themed to the IOSentra glass palette.

import { lazy, Suspense } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { Building2, Server, Activity, PieChart as PieIcon, DollarSign, TrendingUp } from 'lucide-react';
import { systemApi } from '@/api';
import { KpiStat, StatusPill } from '@/components/ui';
import { PageHeader } from '@/components/DataTable';
import { ChartCard, CHART } from '@/components/chart-card';
import { activityIcon } from '@/components/activity-icon';

// recharts is heavy (~200kB gzip); code-split it so the Overview's KPIs paint
// immediately and the chart bundle loads on demand.
const DonutChart = lazy(() => import('@/components/charts').then((m) => ({ default: m.DonutChart })));
const HBarChart = lazy(() => import('@/components/charts').then((m) => ({ default: m.HBarChart })));
const TrendArea = lazy(() => import('@/components/charts').then((m) => ({ default: m.TrendArea })));

const money = (cents: number, currency = 'USD') =>
  new Intl.NumberFormat(undefined, { style: 'currency', currency, maximumFractionDigits: 0 }).format(cents / 100);
const moneyShort = (cents: number) => {
  const d = cents / 100;
  return d >= 1000 ? `$${(d / 1000).toFixed(1)}k` : `$${Math.round(d)}`;
};

export function Dashboard() {
  const { t } = useTranslation(['admin', 'common']);
  const { data: ov, isLoading } = useQuery({ queryKey: ['overview'], queryFn: systemApi.overview, refetchInterval: 30_000 });
  const { data: health } = useQuery({ queryKey: ['health-deep'], queryFn: systemApi.healthDeep, refetchInterval: 30_000 });
  const { data: audit } = useQuery({ queryKey: ['audit-recent'], queryFn: systemApi.audit });

  if (isLoading || !ov) {
    return (
      <div className="space-y-6">
        <PageHeader title={t('dashboard.title')} subtitle={t('dashboard.subtitleLoading')} />
        <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">{Array.from({ length: 4 }).map((_, i) => <div key={i} className="tk-card h-24 animate-pulse" />)}</div>
        <div className="grid gap-4 lg:grid-cols-2">{Array.from({ length: 2 }).map((_, i) => <div key={i} className="tk-card h-64 animate-pulse" />)}</div>
      </div>
    );
  }

  // ── Chart data (all from real endpoint data) ───────────────────────────────
  // 1) Customers by status (donut).
  const statusData = [
    { name: t('dashboard.status.active'), value: ov.byStatus.active, color: CHART.ok },
    { name: t('dashboard.status.provisioning'), value: ov.byStatus.provisioning, color: CHART.primary },
    { name: t('dashboard.status.suspended'), value: ov.byStatus.suspended, color: CHART.warning },
    { name: t('dashboard.status.error'), value: ov.byStatus.error, color: CHART.critical },
    { name: t('dashboard.status.other'), value: ov.byStatus.other, color: CHART.muted },
  ].filter((d) => d.value > 0);

  // 2) MRR by plan (bar) — sum billed price per plan across active subscriptions.
  const planMrr = new Map<string, number>();
  for (const t of ov.perTenant) {
    if (t.priceCents > 0) planMrr.set(t.plan ?? 'unplanned', (planMrr.get(t.plan ?? 'unplanned') ?? 0) + t.priceCents);
  }
  const mrrData = Array.from(planMrr.entries())
    .map(([name, cents]) => ({ name: name.charAt(0).toUpperCase() + name.slice(1), value: cents }))
    .sort((a, b) => b.value - a.value);

  // 3) Top customers by devices (bar) — over-limit customers in red.
  const topDevices = ov.perTenant
    .filter((t) => t.devices > 0)
    .slice(0, 8)
    .map((t) => ({ name: t.name, value: t.devices, color: t.over ? CHART.critical : CHART.primary }));

  // 4) New customers per day over the last 14 days (area) — from provision audit.
  const provTrend = buildProvisioningTrend(audit ?? [], 14);

  const recent = (audit ?? []).slice(0, 8);

  return (
    <div className="space-y-6">
      <PageHeader
        title={t('dashboard.title')}
        subtitle={t('dashboard.subtitle')}
        action={<span className="flex items-center gap-1.5 text-xs text-muted"><span className="tk-pulse-dot h-1.5 w-1.5 rounded-full bg-ok" /> {t('dashboard.live')}</span>}
      />

      {/* Top KPIs */}
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <KpiStat label={t('dashboard.kpi.customers')} value={ov.tenants} tone="info" hint={t('dashboard.kpi.customersHint', { active: ov.byStatus.active, error: ov.byStatus.error, provisioning: ov.byStatus.provisioning })} />
        <KpiStat label={t('dashboard.kpi.mrr')} value={money(ov.mrrCents)} tone="ok" />
        <KpiStat label={t('dashboard.kpi.camerasDevices')} value={`${ov.totalCameras} / ${ov.totalDevices}`} tone="info" hint={t('dashboard.kpi.camerasDevicesHint')} />
        <KpiStat label={t('dashboard.kpi.trials')} value={ov.trialsActive} tone={ov.trialsExpiringSoon ? 'warning' : 'info'} hint={ov.trialsExpiringSoon ? t('dashboard.kpi.trialsExpiring', { count: ov.trialsExpiringSoon }) : t('dashboard.kpi.trialsNoneExpiring')} />
      </div>

      {/* Attention row */}
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <KpiStat label={t('dashboard.kpi.overPlanLimit')} value={ov.overLimitTenants} tone={ov.overLimitTenants ? 'critical' : 'ok'} hint={ov.overLimitTenants ? t('dashboard.kpi.overPlanLimitHint') : t('dashboard.kpi.allWithinPlan')} />
        <KpiStat label={t('dashboard.kpi.suspended')} value={ov.byStatus.suspended} tone={ov.byStatus.suspended ? 'warning' : 'ok'} />
        <div className="tk-card p-4 lg:col-span-2">
          <div className="mb-2 flex items-center gap-2 text-xs text-muted"><Server size={13} /> {t('dashboard.platformServices')}</div>
          <div className="flex flex-wrap gap-2">
            {(health ?? []).map((s) => (
              <span key={s.service} className="flex items-center gap-1.5" title={s.detail}>
                <StatusPill status={s.status} label={s.service.replace(/-/g, ' ')} tone={s.status === 'ok' ? 'ok' : s.status === 'down' ? 'critical' : 'muted'} />
              </span>
            ))}
            {!health && <span className="text-xs text-muted">{t('dashboard.checking')}</span>}
          </div>
        </div>
      </div>

      {/* Charts row 1 — status donut + MRR by plan */}
      <Suspense fallback={<div className="grid gap-4 lg:grid-cols-2"><div className="tk-card h-64 animate-pulse" /><div className="tk-card h-64 animate-pulse" /></div>}>
        <div className="grid gap-4 lg:grid-cols-2">
          <ChartCard title={t('dashboard.charts.customersByStatus')} icon={<PieIcon size={15} />}>
            <DonutChart data={statusData} />
          </ChartCard>
          <ChartCard title={t('dashboard.charts.revenueByPlan')} icon={<DollarSign size={15} />} action={<span className="tk-num text-xs text-muted">{t('dashboard.charts.totalSuffix', { amount: money(ov.mrrCents) })}</span>}>
            <HBarChart data={mrrData} color={CHART.ok} valueFmt={(v) => moneyShort(v)} />
          </ChartCard>
        </div>

        {/* Charts row 2 — top customers + provisioning trend */}
        <div className="grid gap-4 lg:grid-cols-2">
          <ChartCard title={t('dashboard.charts.topCustomersByDevices')} icon={<Building2 size={15} />}>
            <HBarChart data={topDevices} />
          </ChartCard>
          <ChartCard title={t('dashboard.charts.newCustomers14d')} icon={<TrendingUp size={15} />}>
            <TrendArea data={provTrend} />
          </ChartCard>
        </div>
      </Suspense>

      {/* Recent activity */}
      <div className="tk-card p-4">
        <div className="mb-3 flex items-center gap-2 text-sm font-medium"><Activity size={15} /> {t('dashboard.recentActivity')}</div>
        {recent.length === 0 ? <p className="py-6 text-center text-sm text-muted">{t('dashboard.noRecentActivity')}</p> : (
          <ul className="space-y-1">
            {recent.map((a) => {
              const { Icon, tone } = activityIcon(a.action);
              const who = a.actor ?? (a.actorType ? t('dashboard.actorProcess', { type: a.actorType }) : t('dashboard.actorSystem'));
              return (
                <li key={a.id} className="flex items-center gap-3 rounded-lg px-2 py-1.5 text-sm transition-colors hover:bg-white/5">
                  <span className={`grid h-7 w-7 shrink-0 place-items-center rounded-md bg-white/5 ${tone}`}><Icon size={14} /></span>
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-fg">{a.action.replace(/[._]/g, ' ')}</span>
                    <span className="block truncate text-xs text-muted">{t('dashboard.byActor', { who })}</span>
                  </span>
                  <span className="tk-num shrink-0 text-xs text-muted">{new Date(a.createdAt).toLocaleString()}</span>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
}

// Build a per-day count of successful provisions over the last `days` days from
// the audit feed — a real signal (no fabricated series). Buckets by local date.
function buildProvisioningTrend(
  audit: Array<{ action: string; createdAt: string }>,
  days: number,
): Array<{ label: string; value: number }> {
  const buckets = new Map<string, number>();
  const today = new Date();
  // Seed every day with 0 so the line is continuous even with sparse events.
  for (let i = days - 1; i >= 0; i--) {
    const d = new Date(today);
    d.setDate(d.getDate() - i);
    buckets.set(dayKey(d), 0);
  }
  for (const a of audit) {
    if (a.action !== 'tenant.provision.success') continue;
    const key = dayKey(new Date(a.createdAt));
    if (buckets.has(key)) buckets.set(key, (buckets.get(key) ?? 0) + 1);
  }
  return Array.from(buckets.entries()).map(([key, value]) => ({ label: key.slice(5), value }));
}

function dayKey(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}
